import { useContext, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { useMutation, useQuery } from '@apollo/client';
import ButtonBase from '@mui/material/ButtonBase';
import Grid from '@mui/material/Grid';
import Modal from '@mui/material/Modal';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { UPSERT_USER_UPLIANCE_REVIEW } from '../../app/graphql/mutations';
import { FETCH_USER_UPLIANCE_REVIEW } from '../../app/graphql/queries';
import { userIdSelector } from '../../app/main/mainSlice';
import { referralReviewSelector, setReferralReview } from '../../app/recipeCreation/createRecipe.slice';
import { useAppDispatch, useAppSelector } from '../../app/store/hooks';
import useFetchReferralData from '../../hooks/useGetReferralDetails';
import useLogRecipeEdit from '../../hooks/useLogRecipeEdit';
import useTimeoutHandler from '../../hooks/useTimeoutHandler';
import { AlertIcon, CloseModal, HiddenRefferalLink, RefferalBanner, ShareIcon } from '../../iconRef/referralIcons';
import { ErrorManagementContext } from '../../utils/errorManagement/ErrorManagementProvider';
import { GoBackIcon } from '../../utils/iconRefs/recipeRatingIcons';
import styles from './RateUpliance.module.css';
import ReferralLoader from './referralLoader';

const ReviewForm = () => {
    const reviewText = useAppSelector(referralReviewSelector);
    const [review, setReview] = useState<string | null>(reviewText);
    // const [fetchedImage, setFetchedImage] = useState<string | null>(null);
    // const [photo, setPhoto] = useState<string | null>(null);
    const [showExitModal, setShowExitModal] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [pleaseReviewMessage, setPleaseReviewMessage] = useState<boolean>(false);
    const errorMessageText = reviewText && reviewText.length > 15 ? "" : "Review must be at least 15 characters";

    const [errorMessage, setErrorMessage] = useState<string>(errorMessageText);
    const history = useHistory();
    const userId = useAppSelector(userIdSelector);
    const [updateUserUplianceReview] = useMutation(UPSERT_USER_UPLIANCE_REVIEW);
    const { referralLink, title, subtitle, amount, loading: referralLoading, logShareReferralLink } = useFetchReferralData();
    const { cookClubLog } = useContext(ErrorManagementContext);
    const { logRecipeEdit } = useLogRecipeEdit();
    const dispatch = useAppDispatch();
    const [showLoadingModal, setShowLoadingModal] = useState<boolean>(false);
    const [hasCompletedLoop, setHasCompletedLoop] = useState<boolean>(false);
    const [startTimeout] = useTimeoutHandler(() => {
        setHasCompletedLoop(true);
    });
    const [startLoaderTimer] = useTimeoutHandler(() => {
        setShowLoadingModal(false);
        history.push('/account/view-rate-upliance');
    });
    const handleModalClose = () => {
        startLoaderTimer(1250);
    }

    useQuery(FETCH_USER_UPLIANCE_REVIEW, {
        skip: !!reviewText,
        variables: {
            user_id: userId
        },
        onCompleted: (data) => {
            if (data?.User_Upliance_Review[0]?.review_text) {
                setReview(data.User_Upliance_Review[0].review_text);
                dispatch(setReferralReview(data.User_Upliance_Review[0].review_text));
                if (data.User_Upliance_Review[0].review_text.length > 15)
                    setErrorMessage('')
            }
            // if (data?.User_Upliance_Review[0]?.review_image) {
            //     setPhoto(data.data?.User_Upliance_Review[0].review_image);
            //     setFetchedImage(data.data?.User_Upliance_Review[0].review_image)
            // }
        },
        onError: (err) => {
            cookClubLog(
                "",
                "Unable to fetch Review!",
                '',
                { ui: { type: "error" }, browserConsole: true })
            console.error('Error fetching user upliance review', err);
            history.push('/account');

        }
    })

    // const cloudinaryImageUploadHandler = async (imageLink: string | null): Promise<string | null> => {

    //     if (imageLink && imageLink.includes("blob")) {
    //         const cloudinaryImageLink = await cloudinaryuploadedRefferalImage({ imageUrl: imageLink });
    //         imageLink = cloudinaryImageLink?.secure_url;
    //     }
    //     return imageLink;
    // }

    const handleReviewChange = (value: string) => {
        const reviewTextMessage = value ?? "";
        setReview(reviewTextMessage);

        if (value.length > 15) {
            setErrorMessage("");
        } else {
            setErrorMessage("Review must be at least 15 characters");
        }
    };


    const onShareClick = async () => {
        const shareMessage = `${title}
        \n${reviewText}
        \n${subtitle} ${amount}
        \n${referralLink}`;

        try {
            if (navigator.share) {
                const shareData: { text: string } = {
                    text: shareMessage,
                };

                // if (fetchedImage) {
                //     const response = await fetch(fetchedImage);
                //     const blob = await response.blob();
                //     const file = new File([blob], 'review-image.jpg', { type: blob.type });
                //     shareData.files = [file];
                // }

                await navigator.share(shareData);
                logShareReferralLink();
            } else {
                cookClubLog(
                    "",
                    "Sharing is not supported in this browser. Please copy the link manually.",
                    '',
                    { ui: { type: "error" }, browserConsole: true })
            }
        } catch (error) {
            console.error('Error sharing:', error);
        }
    };

    const handleSubmit = async () => {
        const trimmedReview = review?.trim() ?? "";

        if (trimmedReview.length < 15) {
            setErrorMessage("Review must be at least 15 characters");
            return;
        }
        setLoading(true);
        setShowLoadingModal(true);
        startTimeout(1800);
        const currentReview = reviewText;

        dispatch(setReferralReview(trimmedReview));

        if (reviewText) {
            logRecipeEdit('REVIEW_RESUBMITTED', {
                eventData: {
                    review: trimmedReview
                }
            })
        }
        else {
            logRecipeEdit('REVIEW_SUBMIT', {
                eventData: {
                    review: trimmedReview
                }
            })
        }
        try {
            if (userId) {
                await updateUserUplianceReview({
                    variables: {
                        user_id: userId,
                        review_text: trimmedReview,
                        // review_image: uploadedImagesIfBlob
                    }
                });

                setErrorMessage("");
            }
        }
        catch (error) {
            dispatch(setReferralReview(currentReview));
            cookClubLog(
                "",
                "Unable to update review! Please try again later",
                '',
                { ui: { type: "error" }, browserConsole: true })
            console.error("Error updating user review:", error);
        }
        handleModalClose();
        setLoading(false);
    };

    // const handlePhotoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    //     if (event.target.files && event.target.files[0]) {
    //         const file = event.target.files[0];
    //         const imageUrl = URL.createObjectURL(file);
    //         setPhoto(imageUrl);
    //     }
    // };

    // const handleRemovePhoto = () => {
    //     setPhoto(null);
    // };

    const onBack = () => {
        history.goBack()
    }

    const onSkipModal = () => {
        history.goBack()
        logRecipeEdit('SKIP_NOW_POPUP', {})

    }

    const onSkip = () => {
        setShowExitModal(true);
        logRecipeEdit('REVIEW_SKIPPED', {})
    }

    const closeModal = () => {
        setShowExitModal(false);
        logRecipeEdit('KEEP_REVIEWING_CLICKED', {})
    }

    const onHiddenReferralLinkContainer = () => {
        setPleaseReviewMessage(true);
    }

    return (
        <Grid className={styles.container} container>
            <Grid className={styles.referral_banner_container} item>
                <Grid className={styles.close_icon} item />
                <GoBackIcon className={styles.close_icon} onClick={onBack} />
                <RefferalBanner />
                <Grid className={styles.banner_text_container}>
                    <Typography align="center" component="p" variant="popupTitleOutfit">
                        Save a friend
                    </Typography>
                    {!reviewText ? <Typography align="center" component="p" variant="caption3LightOutfit">
                        Enable referral code by sharing your review.
                    </Typography> : null}
                </Grid>
                {reviewText && referralLink ?
                    <Grid className={styles.banner_refferal_container}>
                        <Grid className={styles.refferal_link_container}>
                            <Typography className={styles.referral_text} variant='ingredientText2paralellOutfit'>{referralLoading ? "loading..." : referralLink}</Typography>
                            <ButtonBase className={styles.share_btn} disabled={referralLoading} onClick={onShareClick}>
                                <Typography className={styles.share_btn_text} variant='caption4LightOutfit'>
                                    Share
                                </Typography>
                                <ShareIcon />
                            </ButtonBase>
                        </Grid>
                        <Grid className={styles.referral_alert_container}>
                            <AlertIcon />
                            <Typography className={styles.refferal_info_text} variant='caption4LightOutfit'>This link is valid for 24 hours only</Typography>
                        </Grid>
                    </Grid>
                    :
                    <Grid className={styles.banner_refferal_container} >
                        <ButtonBase onClick={onHiddenReferralLinkContainer}>
                            <HiddenRefferalLink />
                        </ButtonBase>
                        {pleaseReviewMessage ?
                            <Grid className={styles.referral_alert_container}>
                                <AlertIcon />
                                <Typography className={styles.disabled_refferal_info_text} variant='caption4LightOutfit'>Please submit a review to access your referral code.</Typography>
                            </Grid>
                            : null}
                    </Grid>
                }
            </Grid>

            <Grid className={styles.referral_form_container} item>
                <Grid item>
                    <Typography className={styles.heading} variant="cardOutfit">
                        Tell us how Upliance has helped in your kitchen
                    </Typography>
                </Grid>

                <Grid className={styles.text_area_container} item>
                    <Grid
                        className={styles.textfield_outer_container}
                    >
                        <TextField
                            className={styles.text_area_textfield}
                            fullWidth
                            inputProps={{ maxLength: 500 }}
                            InputProps={{
                                sx: {
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        border: 'none',
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        border: 'none',
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        border: 'none',
                                    },
                                    '& .MuiInputBase-input': {
                                        color: '#6D7699',
                                        fontFamily: 'Outfit',
                                        fontSize: '14px',
                                        fontWeight: '400',
                                    },

                                },
                            }}
                            multiline
                            onChange={(e) => handleReviewChange(e?.target?.value)}
                            placeholder="Type here"
                            value={review}
                            variant="outlined"
                        />
                        <Typography
                            className={`${styles.helper_text} ${errorMessage ? styles.error_message : ''}`}
                            variant="caption4Outfit"
                        >
                            {errorMessage || `${review?.length}/500`}
                        </Typography>
                    </Grid>
                </Grid>

                {/* <Grid item container direction="row" alignItems="center" spacing={1}>
                    {photo ? (
                        <Grid className={styles.referral_img_container} onClick={handleRemovePhoto}>
                            <img
                                src={photo}
                                className={styles.referral_img}
                            />
                            <RemoveImage className={styles.delete_icon} />
                        </Grid>
                    ) : (
                        <Grid item xs={12}>
                            <input
                                accept="image/*"
                                style={{ display: 'none' }}
                                id="upload-photo"
                                type="file"
                                onChange={handlePhotoUpload}
                            />
                            <label htmlFor="upload-photo">
                                <Button
                                    component="span"
                                    startIcon={<Camera />}
                                    className={styles.add_photo_button}
                                >
                                    <Typography variant='caption4Light' >Add photo</Typography>
                                </Button>
                            </label>
                        </Grid>
                    )}
                </Grid> */}

                <Grid className={styles.referral_button_container} container item>
                    <ButtonBase className={`${styles.referral_button} ${styles.skip_btn}`} onClick={onSkip}>
                        <Typography variant='ingredientText1Outfit'>Skip for now</Typography>
                    </ButtonBase>
                    {/* Add this condition while adding image (fetchedImage === photo || (fetchedReview === null && fetchedImage === null)) */}
                    <ButtonBase
                        className={`${styles.referral_button} ${reviewText === review || !!errorMessage ? styles.disabled_btn : styles.submit_btn}`}
                        disabled={reviewText === review || !!errorMessage}
                        onClick={handleSubmit}
                    >
                        <Typography variant='ingredientText1Outfit'>{reviewText ? "Re-submit" : "Submit"}</Typography>
                    </ButtonBase>
                </Grid>
            </Grid>
            <Modal
                onClose={closeModal}
                open={showExitModal}
            >
                <Grid className={styles.exit_modal}>
                    <Grid className={styles.exit_modal_container}>
                        <CloseModal className={styles.exit_modal_icon} onClick={closeModal} />
                        <Grid className={styles.exit_modal_text}>
                            <Typography component="p" variant='referralPopupText'>Are you sure you want to go back</Typography>
                        </Grid>
                        <Typography className={styles.close_modal_info} component="p" variant='caption4LightOutfit'>If you go back now, your review won't be saved. Do you want to continue?</Typography>
                    </Grid>
                    <Grid className={styles.referral_button_container} container item>
                        <ButtonBase className={`${styles.referral_button} ${styles.modal_skip_btn}`} onClick={onSkipModal}>
                            <Typography variant='ingredientText1Outfit'>Skip for now</Typography>
                        </ButtonBase>
                        <ButtonBase className={`${styles.referral_button} ${styles.submit_btn}`} onClick={closeModal}>
                            <Typography variant='ingredientText1Outfit'>Keep Reviewing</Typography>
                        </ButtonBase>
                    </Grid>
                </Grid>
            </Modal>
            {showLoadingModal ? <ReferralLoader hasCompletedLoop={hasCompletedLoop} loading={loading} /> : null}      </Grid>
    );
};

export default ReviewForm;
